import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/brand-logo";
import { useAuth, ROLE_HOME, type Role } from "@/lib/auth";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ensureDemoAccounts } from "@/lib/demo-setup.functions";
import { seedDemoData } from "@/lib/demo-seed.functions";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/lib/theme";

const NAV_ITEMS: { label: string; href: string }[] = [
  { label: "Tính năng", href: "#features" },
  { label: "Bảng giá", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Liên hệ", href: "#contact" },
];

const EASE = [0.32, 0.72, 0, 1] as const;

export function LandingNav() {
  const [mobile, setMobile] = React.useState(false);
  const [hidden, setHidden] = React.useState(false);
  const [demoLoading, setDemoLoading] = React.useState<Role | null>(null);
  const { demoSignIn } = useAuth();
  const navigate = useNavigate();
  const ensureAccounts = useServerFn(ensureDemoAccounts);
  const seed = useServerFn(seedDemoData);
  const lastY = React.useRef(0);

  React.useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastY.current && y > 200);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const pickDemo = async (r: Role) => {
    if (demoLoading) return;
    setDemoLoading(r);
    try {
      await ensureAccounts({ data: undefined });
      const { count } = await supabase.from("profiles").select("id", { count: "exact", head: true });
      if ((count ?? 0) < 50) {
        await demoSignIn("admin");
        await seed({ data: undefined });
      }
      await demoSignIn(r);
      setMobile(false);
      navigate({ to: ROLE_HOME[r] });
    } catch (e) {
      console.error(e);
      toast.error("Không thể tải demo. Vui lòng thử lại.");
    } finally {
      setDemoLoading(null);
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: hidden ? -100 : 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 px-2 py-1.5 rounded-full bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl ring-1 ring-black/[0.04] dark:ring-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] max-w-[calc(100vw-1rem)]"
      >
        <Link to="/" className="flex items-center gap-2 px-2.5">
          <BrandLogo size="size-8" />
          <span className="font-display font-semibold text-[15px] tracking-tight text-slate-900 dark:text-white">
            Ezyclass
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-0.5 pl-2">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="px-3.5 py-1.5 rounded-full text-[13px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white hover:bg-slate-100 dark:bg-slate-800/80 transition-all duration-300"
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="hidden sm:flex items-center gap-1 ml-1 pl-1 border-l border-black/[0.04] dark:border-white/10">
          <ThemeToggle className="!size-8 mr-1" />
          <button
            disabled={demoLoading !== null}
            onClick={() => pickDemo("admin")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white transition disabled:opacity-60"
          >
            {demoLoading ? (
              <Loader2 className="size-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <Sparkles className="size-3.5" strokeWidth={1.5} />
            )}
            Demo
          </button>
          <Link
            to="/login"
            className="px-3 py-1.5 rounded-full text-[13px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white transition"
          >
            Đăng nhập
          </Link>
          <Link
            to="/signup"
            className="group flex items-center gap-1.5 pl-4 pr-1.5 py-1.5 rounded-full bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-800 transition-all duration-500"
            style={{ transitionTimingFunction: "cubic-bezier(0.32,0.72,0,1)" }}
          >
            Bắt đầu
            <span className="size-6 rounded-full bg-white/15 grid place-items-center group-hover:translate-x-0.5 transition-transform duration-500">
              <ArrowRight className="size-3.5" strokeWidth={1.5} />
            </span>
          </Link>
        </div>

        <button
          onClick={() => setMobile((v) => !v)}
          className="lg:hidden ml-1 p-2 rounded-full text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:bg-slate-800/80 transition"
          aria-label="Menu"
        >
          {mobile ? <X className="size-4" strokeWidth={1.5} /> : <Menu className="size-4" strokeWidth={1.5} />}
        </button>
      </motion.nav>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="lg:hidden fixed top-20 left-4 right-4 z-40 p-2 rounded-3xl bg-white/90 backdrop-blur-xl ring-1 ring-black/[0.04] dark:ring-white/10 shadow-[0_12px_40px_rgb(0,0,0,0.08)]"
          >
            <div className="flex flex-col">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobile(false)}
                  className="px-4 py-3 rounded-2xl text-[14px] font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:bg-slate-800/80 transition"
                >
                  {item.label}
                </a>
              ))}
              <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
              <button
                disabled={demoLoading !== null}
                onClick={() => pickDemo("admin")}
                className="flex items-center gap-2 px-4 py-3 rounded-2xl text-[14px] font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:bg-slate-800/80 transition text-left disabled:opacity-60"
              >
                {demoLoading ? (
                  <Loader2 className="size-4 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Sparkles className="size-4" strokeWidth={1.5} />
                )}
                Xem demo
              </button>
              <Link
                to="/login"
                onClick={() => setMobile(false)}
                className="px-4 py-3 rounded-2xl text-[14px] font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:bg-slate-800/80 transition"
              >
                Đăng nhập
              </Link>
              <Link
                to="/signup"
                onClick={() => setMobile(false)}
                className="mt-1 flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl bg-slate-900 text-white text-[14px] font-medium hover:bg-slate-800 transition"
              >
                Bắt đầu miễn phí
                <ArrowRight className="size-4" strokeWidth={1.5} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
